import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Popover } from '@progress/kendo-react-tooltip';
import dayjs from 'dayjs';
import React from 'react';
import { AppState, IntegrationStatus } from '../../../common/enums';
import { MapNavSettingsContextMenu } from '../../../components/map-interaction/context-menu/MapNavSettingsContextMenu';
import useSelectedWeather from '../../../components/weather/hooks/use-selected-weather';
import useDebugFeatures from '../../../hooks/use-debug-features';
import useIntegration from '../../../hooks/use-integration';
import useTimeNow from '../../../hooks/use-time-now';
import { ArrowUpOutline, LockSolid } from '../../../icons/icons';
import { MyFleetAirportModeOptions } from '../../../types/dropdown-options';
import { MyFleetMode } from '../../../udView/udview';
import { useCanSaveView, useFocusedWorkspaceSelector } from '../../features/home/hooks/use-view';
import { useAppSelector } from '../../hooks/store-hooks';
import { useDateTimeFormatter } from '../../hooks/use-date-time-formatted';
import { useMapSourceSettings } from '../../hooks/use-map-settings';
import { LoadingIndicatorCircular } from '../indicators/LoadingIndicatorCircular';
import CustomPopover from '../layout/CustomPopover';

const appStateString: Record<AppState, string> = {
    [AppState.Starting]: 'Starting...',
    [AppState.Intro]: 'Intro...',
    [AppState.Connecting]: 'Connecting...',
    [AppState.Logging]: 'Logging...',
    [AppState.Running]: 'Running',
    [AppState.Reconnecting]: 'Reconnecting...',
    [AppState.LoginFailed]: 'Login failed'
};

export function AppStateStatusBarItem() {
    const state = useAppSelector((x) => x.global.flightWorker.state);
    const isPlayback = useAppSelector((x) => x.global.playback.active);
    const isRecording = useAppSelector((x) => x.global.recording.active);

    if (isPlayback) {
        return <div>Running - Playback</div>;
    }

    return (
        <div className={state.type === AppState.LoginFailed ? 'text-warning' : ''}>
            <SvgIcon className="icon-b-1" icon={LockSolid} style={{ width: 12, height: 12 }} />
            <span className="ps-1">
                {state.type === AppState.Reconnecting ? (
                    <>Reconnecting({state.attempt})...</>
                ) : state.type === AppState.LoginFailed ? (
                    <>Login failed ({state.code})</>
                ) : (
                    appStateString[state.type]
                )}
            </span>

            {isRecording && <span> - Recording</span>}
        </div>
    );
}

export function IntegrationStatusBarItem() {
    const integrationInfo = useIntegration();
    const integrationId = useAppSelector((x) => x.settings.integrationId);

    const statusText = React.useMemo(() => {
        if (!integrationInfo.enabled) {
            return '';
        }
        switch (integrationInfo.status) {
            case IntegrationStatus.Ok:
                return integrationId || 'ok';
            case IntegrationStatus.NoRights:
                return 'no auth';
            case IntegrationStatus.Duplicate:
                return 'ID in use';
            default:
                return 'no reply';
        }
    }, [integrationInfo, integrationId]);

    return integrationInfo.enabled ? (
        <div>
            <div>
                {integrationInfo.typeText} {statusText}
            </div>
        </div>
    ) : null;
}

export function MyFleetStatusBarItem() {
    const myFleetLoaded = useAppSelector((x) => x.global.flightWorker.myFleetLoaded);
    const myFleetMode = useAppSelector((x) => x.settings.myFleetMode);
    const myFleetAirport = useAppSelector((x) => x.settings.myFleetAirport);
    const myFleetAirportMode = useAppSelector((x) => x.settings.myFleetAirportMode);
    const myFleetAirlines = useAppSelector((x) => x.settings.myFleetAirlines);
    const myFleetDeskFilter = useAppSelector((x) => x.settings.myFleetDeskFilter);

    let myFleetString = '';
    switch (myFleetMode) {
        case MyFleetMode.disabled:
            myFleetString = '-';
            break;

        case MyFleetMode.acid:
            myFleetString = 'list';
            break;

        case MyFleetMode.airline:
            myFleetString = myFleetAirlines.join(', ');
            break;

        case MyFleetMode.airport:
            myFleetString = `${myFleetAirport || '?'} (${MyFleetAirportModeOptions.find((x) => x.value === myFleetAirportMode)?.text})`;
            break;
    }

    if (myFleetDeskFilter.length > 0) {
        myFleetString += ', desk: ' + myFleetDeskFilter.join('/');
    }

    return (
        <div>
            <span>My fleet: {myFleetString}</span>
            {!myFleetLoaded && <LoadingIndicatorCircular className="ms-2" size="0.8rem" />}
        </div>
    );
}

export function UDViewStatusBarItem() {
    const viewName = useFocusedWorkspaceSelector((x) => x.view.name);
    const canSaveView = useCanSaveView();
    const slideshow = useAppSelector((x) => x.global.slideshow);

    return (
        <div className="w-100">
            View{slideshow ? ' (slideshow)' : ''}: {viewName} {canSaveView && '*'}
        </div>
    );
}

const navRedAgeSeconds = 30 * 3600;
const navYellowAgeSeconds = 7 * 3600;
export function NavDataVersionStatusBarItem() {
    const mapSource = useMapSourceSettings();
    const formatter = useDateTimeFormatter();
    const debug = useDebugFeatures();
    let color: string | undefined = undefined;
    if (mapSource.updateAvailable) {
        const age = dayjs().unix() - mapSource.nav.buildDate;
        color = age >= navRedAgeSeconds ? 'var(--color-semantic-red)' : age >= navYellowAgeSeconds ? 'var(--color-semantic-yellow)' : '';
    }
    return (
        <div>
            <span style={{ color }}>Nav: {formatter.formatDate(mapSource.nav.cycleDate)}</span>
            {(mapSource.updateAvailable || debug) && (
                <CustomPopover
                    closeOnClick={false}
                    offsetOptions={{ mainAxis: 5 }}
                    animateTranslate={false}
                    placement="bottom"
                    trigger={
                        <Button
                            svgIcon={ArrowUpOutline}
                            className="button-small ms-2 icon-b-1 k-rounded-full"
                            fillMode={'outline'}
                            themeColor={'success'}
                            style={{ width: 12, height: 12 }}
                        />
                    }>
                    <MapNavSettingsContextMenu />
                </CustomPopover>
            )}
        </div>
    );
}

export function WeatherStatusBarItem() {
    const selectedProducts = useSelectedWeather({ ignoreEvents: ['visibility', 'visibleFrameChanged'] });
    const hasErrors = React.useMemo(() => selectedProducts.some((x) => x.loaded && !x.success), [selectedProducts]);
    const element = React.useRef<HTMLDivElement>(null);
    const [show, setShow] = React.useState(false);

    return (
        <div>
            <Popover animate={false} show={show && hasErrors} anchor={element.current} position="top">
                <div>
                    {selectedProducts
                        .filter((x) => x.loaded && !x.success)
                        .map((p) => (
                            <div>
                                <span>
                                    {p.product.name} [{p.product.vendor}]
                                </span>
                                <span className="danger-text"> {p.error}</span>
                            </div>
                        ))}
                </div>
            </Popover>

            <span ref={element} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
                <SvgIcon className="icon-b-1" icon={LockSolid} style={{ width: 12, height: 12 }} />
                <span className="ps-1">Weather: </span>
                {hasErrors && <span className="danger-text">Error</span>}
                {!hasErrors && <span>Current</span>}
            </span>
        </div>
    );
}

export function FlightCountsStatusBarItem() {
    const focusedWorkspaceItem = useAppSelector((x) => x.workspace.selectedId);
    const lastUpdate = useAppSelector((x) => x.global.flightWorker.lastUpdate);
    return (
        <div style={{ minWidth: '33ch' }}>
            <span>
                Active: {lastUpdate?.workspaceFilterCounts[focusedWorkspaceItem]?.active ?? '-'}/{lastUpdate?.activeTotalCount ?? '-'}
            </span>
            <span className="ps-3">
                PreDep: {lastUpdate?.workspaceFilterCounts[focusedWorkspaceItem]?.preDep ?? '-'}/{lastUpdate?.preDepartureTotalCount ?? '-'}
            </span>
        </div>
    );
}

export function CurrentTimeStatusBarItem() {
    const formatter = useDateTimeFormatter();
    const now = useTimeNow({ intervalSeconds: 1 });
    const playback = useAppSelector((x) => x.global.playback.active);
    return (
        <div style={{ minWidth: '20ch' }}>
            {playback ? 'Playback time' : 'Current time'}: {formatter.formatTime(now, 'hmsnd')}
        </div>
    );
}

export function LastUpdateTimeStatusBarItem() {
    const formatter = useDateTimeFormatter();
    const lastUpdate = useAppSelector((x) => x.global.flightWorker.lastUpdate?.time);
    const now = useTimeNow({ intervalSeconds: 1 });
    const playbackActive = useAppSelector((x) => x.global.playback.active);
    let color: string | undefined = undefined;
    if (lastUpdate && !playbackActive) {
        const timeDiff = now - lastUpdate;
        if (timeDiff > 300) {
            color = 'var(--color-red-400)';
        } else if (timeDiff > 150) {
            color = 'var(--color-yellow-400)';
        }
    }

    return <div style={{ color, minWidth: '22ch' }}>Last update: {formatter.formatTime(lastUpdate, 'hms') || '-'}</div>;
}
